import { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, Power, PowerOff, Pencil, RefreshCw, Lock, ArrowLeft,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import Ayuda from '../ui/Ayuda';
import ChipEstado from '../ui/ChipEstado';
import Motivo from '../experiencia/Motivo';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import AvisoNoSeEjecutaSola from './AvisoNoSeEjecutaSola';
import {
  PERMISO_REGLAS, UNIDADES, cambiosDeRegla, cifraLegible, condicionLegible,
  leerHoras, leerUmbral, textoInicial,
} from './umbral';

/* =====================================================================
   REGLAS DE AUTORIZACIÓN · EX-5 y AC-13 (1 de octubre de 2026)
   =====================================================================
   Qué acciones de la empresa esperan una firma, a partir de cuánto y
   quién la da. Hasta hoy esto solo se podía cambiar por la API: la nota
   de «Firmas pendientes» decía «encendidas en Reglas de autorización» y
   «Reglas para todas las empresas del grupo» mandaba a «Autorizaciones →
   Reglas», y ninguna de las dos pantallas existía.

   USA LA API DE SIEMPRE, SIN CAMBIARLE EL CONTRATO:
     GET /api/autorizaciones/reglas/todas        (la lista)
     PUT /api/autorizaciones/reglas/{id_regla}   (encender, apagar, ajustar)
   Las dos piden el permiso `autorizaciones.reglas`. Quien no lo tiene no
   ve una pantalla rota ni un 403: ve qué es esto y a quién pedírselo.

   LA CIFRA SE ESCRIBE COMO CIFRA (AC-13). «$5,000», no
   {"valor_minimo": 5000}: `umbral.js` traduce en las dos direcciones
   según la unidad de cada tipo de acción, que es un dato del servidor.

   CAMBIAR UNA REGLA TAMBIÉN PUEDE PEDIR FIRMA, si la empresa tiene
   encendida la de «Cambiar una regla de autorización». El servidor
   contesta entonces «Enviado a autorización» y la regla NO cambia; la
   pantalla lo enseña tal cual, en ámbar, y no como hecho.
   ===================================================================== */

const ESTADO_REGLA = {
  ENCENDIDA: { etiqueta: 'Encendida', color: 'verde' },
  APAGADA: { etiqueta: 'Apagada', color: 'gris' },
};

const BOTON = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold';
const BOTON_PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white disabled:opacity-50`;
const BOTON_SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800 disabled:opacity-50`;

function mensajeDeError(e, omision) {
  return e instanceof ErrorApi && e.message ? e.message : omision;
}

export default function ReglasDeAutorizacion({ irA }) {
  const { puede } = useAuth();
  // Sin la función (una sesión vieja), se pregunta al servidor: él decide.
  const tienePermiso = typeof puede === 'function' ? puede(PERMISO_REGLAS) : true;

  const [reglas, setReglas] = useState([]);
  const [cargando, setCargando] = useState(tienePermiso);
  const [error, setError] = useState(null);
  const [sinPermiso, setSinPermiso] = useState(!tienePermiso);
  const [editando, setEditando] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const [hecho, setHecho] = useState(null);

  const pedir = useCallback(() => api.get('/autorizaciones/reglas/todas'), []);

  useEffect(() => {
    if (!tienePermiso) return undefined;
    let vigente = true;
    pedir()
      .then((d) => { if (vigente) { setReglas(Array.isArray(d) ? d : []); setError(null); } })
      .catch((e) => {
        if (!vigente) return;
        if (e?.status === 403) setSinPermiso(true); else setError(e);
      })
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [tienePermiso, pedir]);

  const recargar = useCallback(() => {
    setCargando(true);
    return pedir()
      .then((d) => { setReglas(Array.isArray(d) ? d : []); setError(null); })
      .catch((e) => { if (e?.status === 403) setSinPermiso(true); else setError(e); })
      .finally(() => setCargando(false));
  }, [pedir]);

  // Guardar es lo mismo desde el interruptor y desde el formulario: el PUT
  // de siempre con solo lo que cambió. El mensaje es el del servidor, que
  // es quien sabe si cambió o si quedó esperando firma.
  async function guardar(regla, cambios) {
    const r = await api.put(`/autorizaciones/reglas/${regla.id}`, cambios);
    const pendiente = r?.estado === 'PENDIENTE_APROBACION';
    setHecho({ pendiente, texto: `${regla.nombre_accion}: ${r?.mensaje || 'Regla actualizada.'}` });
    await recargar();
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-900">Reglas de autorización</h2>
        <p className="text-sm text-gray-500">
          Qué acciones de tu empresa esperan una firma antes de hacerse, a
          partir de cuánto y quién la da.
        </p>
        <Ayuda id="autorizaciones.reglas.pantalla" />
      </header>

      {hecho && (
        <div
          role="status"
          className={`rounded-xl border p-3 text-sm ${hecho.pendiente
            ? 'border-amber-200 bg-amber-50 text-amber-900'
            : 'border-green-200 bg-green-50 text-green-900'}`}
        >
          {hecho.texto}
        </div>
      )}

      {sinPermiso && (
        <EstadoVacio
          icono={Lock}
          titulo="Las reglas las ve y las cambia quien tiene permiso"
          descripcion={'Para abrirlas hace falta el permiso «Cambiar las reglas de '
            + 'autorización». Pídeselo a quien administra a tu personal: se concede '
            + 'sin cambiarte de puesto.'}
          accion={irA && (
            <button type="button" onClick={() => irA('autorizaciones')} className={BOTON_SECUNDARIO}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Ir a Firmas pendientes
            </button>
          )}
        />
      )}

      {!sinPermiso && error && !cargando && (
        <ErrorConReintento que="No se pudieron cargar las reglas" porque={error} onReintentar={recargar} />
      )}

      {!sinPermiso && cargando && <Cargando filas={5} etiqueta="Cargando las reglas" />}

      {!sinPermiso && !cargando && !error && reglas.length === 0 && (
        <EstadoVacio
          icono={ShieldCheck}
          titulo="No aparece ninguna regla"
          descripcion={'TEGO crea las reglas de tu empresa al darla de alta, todas '
            + 'apagadas. Vuelve a cargar la lista; si sigue vacía, avisa desde '
            + '«Pedir ayuda a TI».'}
          accion={(
            <button type="button" onClick={recargar} className={BOTON_SECUNDARIO}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Volver a cargar
            </button>
          )}
        />
      )}

      {!sinPermiso && !cargando && !error && reglas.length > 0 && (
        <ul className="space-y-3">
          {reglas.map((r) => (
            <TarjetaRegla
              key={r.id}
              regla={r}
              onEncender={() => { setHecho(null); setConfirmar(r); }}
              onCambiar={() => { setHecho(null); setEditando(r); }}
            />
          ))}
        </ul>
      )}

      {confirmar && (
        <ConfirmarEncendido
          regla={confirmar}
          onCerrar={() => setConfirmar(null)}
          onConfirmar={async () => { await guardar(confirmar, { activa: !confirmar.activa }); setConfirmar(null); }}
        />
      )}

      {editando && (
        <FormularioRegla
          regla={editando}
          onCerrar={() => setEditando(null)}
          onGuardar={async (cambios) => { await guardar(editando, cambios); setEditando(null); }}
        />
      )}
    </div>
  );
}

function Dato({ campo, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2">
      <dt className="text-gray-500 sm:w-40 shrink-0">{campo}</dt>
      <dd className="text-gray-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

function firmasLegibles(n) {
  return n === 1 ? 'Una firma' : `${n} firmas de personas distintas`;
}

function TarjetaRegla({ regla: r, onEncender, onCambiar }) {
  const nombres = Array.isArray(r.quienes_firman) ? r.quienes_firman : null;
  const permiso = r.nombre_permiso || r.permiso_para_aprobar;
  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">{r.nombre_accion}</h3>
          <p className="text-sm text-gray-600">{r.descripcion}</p>
        </div>
        <ChipEstado mapa={ESTADO_REGLA} clave={r.activa ? 'ENCENDIDA' : 'APAGADA'} />
      </div>

      <dl className="space-y-1.5 text-sm">
        <Dato campo="Cuándo pide firma">{condicionLegible(r.condicion, r.unidad)}</Dato>
        <Dato campo="Cuántas firmas">{firmasLegibles(r.aprobadores_requeridos)}</Dato>
        <Dato campo="Plazo para decidir">{r.plazo_horas} horas</Dato>
        <Dato campo="Quién la firma">
          {nombres
            ? (nombres.length ? nombres.join(', ') : 'Hoy nadie tiene el permiso para firmarla')
            : `${r.aprobadores_posibles} persona(s) con permiso`}
          <span className="block text-xs text-gray-500">
            Quien tenga el permiso «{permiso}». Se concede en Personal.
          </span>
        </Dato>
      </dl>

      {!r.se_ejecuta_sola && <AvisoNoSeEjecutaSola />}

      {r.aviso && (
        <p className="text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          {r.aviso}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onEncender} className={BOTON_SECUNDARIO}>
          {r.activa
            ? <><PowerOff className="h-4 w-4" aria-hidden="true" /> Apagar</>
            : <><Power className="h-4 w-4" aria-hidden="true" /> Encender</>}
        </button>
        <button type="button" onClick={onCambiar} className={BOTON_SECUNDARIO}>
          <Pencil className="h-4 w-4" aria-hidden="true" /> Cambiar cifra, firmas o plazo
        </button>
      </div>
    </li>
  );
}

// TODA ACCIÓN RIESGOSA PIDE CONFIRMACIÓN EXPLICANDO LA CONSECUENCIA EN UNA
// FRASE. Y si encenderla deja las solicitudes sin quien las firme, se dice
// aquí, delante del botón que lo provocaría.
function ConfirmarEncendido({ regla, onCerrar, onConfirmar }) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const encender = !regla.activa;
  const cuando = condicionLegible(regla.condicion, regla.unidad).toLowerCase();

  async function confirmar() {
    setEnviando(true);
    setError('');
    try {
      await onConfirmar();
    } catch (e) {
      setError(mensajeDeError(e, 'No se guardó el cambio: la conexión con TEGO se cortó. Vuelve a intentarlo.'));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo={encender ? 'Encender esta regla' : 'Apagar esta regla'} onCerrar={onCerrar}>
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-gray-800">
          {encender
            ? `Desde ahora, «${regla.nombre_accion}» espera una firma antes de hacerse (${cuando}).`
            : `Desde ahora, «${regla.nombre_accion}» se hace sin esperar firma. Lo que ya se pidió sigue su curso.`}
        </p>
        {encender && regla.aviso && (
          <p className="text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            {regla.aviso}
          </p>
        )}
        {encender && !regla.se_ejecuta_sola && <AvisoNoSeEjecutaSola />}
        {encender && <Ayuda id="autorizaciones.reglas.el_dueno" />}
        {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800">{error}</div>}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={confirmar} disabled={enviando} className={BOTON_PRIMARIO}>
            {enviando ? 'Guardando…' : (encender ? 'Sí, encenderla' : 'Sí, apagarla')}
          </button>
          <button type="button" onClick={onCerrar} className={BOTON_SECUNDARIO}>Mejor no</button>
        </div>
      </div>
    </Modal>
  );
}

export function FormularioRegla({ regla, onCerrar, onGuardar }) {
  const unidad = UNIDADES[regla.unidad];
  const [umbral, setUmbral] = useState(() => textoInicial(regla.condicion, regla.unidad));
  const [firmas, setFirmas] = useState(String(regla.aprobadores_requeridos || 1));
  const [horas, setHoras] = useState(String(regla.plazo_horas ?? 48));
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  // VALIDACIÓN EN VIVO, mientras se escribe, no al guardar.
  const lectura = leerUmbral(umbral, regla.unidad);
  const plazo = leerHoras(horas);
  const cambios = cambiosDeRegla(regla, { umbral, firmas, horas });
  const sinCambios = Object.keys(cambios).length === 0;
  const falta = lectura.error || plazo.error
    || (sinCambios ? 'Cambia la cifra, las firmas o el plazo para poder guardar.' : '');

  async function enviar() {
    if (falta || enviando) return;
    setEnviando(true);
    setError('');
    try {
      const m = motivo.trim();
      await onGuardar(m ? { ...cambios, motivo: m } : cambios);
    } catch (e) {
      setError(mensajeDeError(e, 'No se guardó el cambio: la conexión con TEGO se cortó. Vuelve a intentarlo.'));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo={regla.nombre_accion} onCerrar={onCerrar}>
      <div className="space-y-4">
        {unidad ? (
          <div>
            <label htmlFor="regla-umbral" className="block text-sm font-semibold text-gray-900">
              {unidad.etiqueta}
            </label>
            <input
              id="regla-umbral"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={umbral}
              onChange={(e) => setUmbral(e.target.value)}
              aria-invalid={lectura.error ? 'true' : 'false'}
              aria-describedby="regla-umbral-lectura"
              placeholder={`Ejemplo: ${unidad.ejemplo}`}
              className={`mt-1 w-full rounded-xl border px-3 py-2.5 text-base ${lectura.error ? 'border-red-400' : 'border-gray-300'}`}
            />
            <p id="regla-umbral-lectura" aria-live="polite"
               className={`mt-1 text-sm ${lectura.error ? 'text-red-700' : 'text-gray-700'}`}>
              {lectura.error || (lectura.valor === null
                ? 'Pedirá firma siempre, sin importar el importe.'
                : `Pedirá firma a partir de ${cifraLegible(lectura.valor, regla.unidad)}.`)}
            </p>
            <Ayuda id="autorizaciones.reglas.umbral" />
          </div>
        ) : (
          <Ayuda id="autorizaciones.reglas.sin_importe" />
        )}

        <div>
          <label htmlFor="regla-firmas" className="block text-sm font-semibold text-gray-900">
            Cuántas firmas hacen falta
          </label>
          <select
            id="regla-firmas"
            value={firmas}
            onChange={(e) => setFirmas(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base"
          >
            <option value="1">Una</option>
            <option value="2">Dos</option>
            <option value="3">Tres</option>
          </select>
          <Ayuda id="autorizaciones.reglas.firmas" />
        </div>

        <div>
          <label htmlFor="regla-horas" className="block text-sm font-semibold text-gray-900">
            Horas para decidir
          </label>
          <input
            id="regla-horas"
            type="text"
            inputMode="numeric"
            value={horas}
            onChange={(e) => setHoras(e.target.value)}
            aria-invalid={plazo.error ? 'true' : 'false'}
            className={`mt-1 w-full rounded-xl border px-3 py-2.5 text-base ${plazo.error ? 'border-red-400' : 'border-gray-300'}`}
          />
          {plazo.error && <p className="mt-1 text-sm text-red-700">{plazo.error}</p>}
          <Ayuda id="autorizaciones.reglas.plazo" />
        </div>

        <div>
          <label htmlFor="regla-motivo" className="block text-sm font-semibold text-gray-900">
            Por qué la cambias (opcional)
          </label>
          <input
            id="regla-motivo"
            type="text"
            maxLength={300}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ejemplo: los pagos grandes los firmo yo"
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base"
          />
          <Ayuda id="autorizaciones.reglas.motivo" />
        </div>

        {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800">{error}</div>}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={enviar}
            disabled={!!falta || enviando}
            aria-describedby="regla-guardar-motivo"
            className={BOTON_PRIMARIO}
          >
            {enviando ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <button type="button" onClick={onCerrar} className={BOTON_SECUNDARIO}>Cancelar</button>
          <Motivo id="regla-guardar-motivo" cuando={!!falta} className="basis-full">{falta}</Motivo>
        </div>
      </div>
    </Modal>
  );
}
